/**
 * ClientDataEditors - Dialog components for editing client's shared data
 *
 * Provides modal dialogs for:
 * - Editing credits list
 * - Editing scoring data
 * - Editing creditworthiness
 * - Editing personal data
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from "@/shared/components/ui";
import { Plus, Trash2, Save, AlertCircle } from "lucide-react";
import type {
  SharedCredit,
  ScoringResult,
  CreditworthinessResult,
  PersonalDataSnapshot,
} from "@/shared/types";

// ============================================
// Credits Editor
// ============================================

interface CreditsEditorProps {
  isOpen: boolean;
  onClose: () => void;
  credits: SharedCredit[];
  onSave: (credits: SharedCredit[]) => Promise<void>;
  isSaving: boolean;
}

export function CreditsEditorDialog({
  isOpen,
  onClose,
  credits: initialCredits,
  onSave,
  isSaving,
}: CreditsEditorProps) {
  const [credits, setCredits] = useState<SharedCredit[]>(initialCredits);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCredits(initialCredits);
  }, [initialCredits]);

  const handleAddCredit = () => {
    const newCredit: SharedCredit = {
      id: `credit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: "Gotówkowy",
      bank: "",
      remainingAmount: 0,
      monthlyPayment: 0,
    };
    setCredits([...credits, newCredit]);
  };

  const handleRemoveCredit = (id: string) => {
    setCredits(credits.filter((c) => c.id !== id));
  };

  const handleUpdateCredit = (id: string, updates: Partial<SharedCredit>) => {
    setCredits(credits.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const handleSave = async () => {
    try {
      setError(null);
      await onSave(credits);
      onClose();
    } catch (e) {
      setError((e as Error).message || "Wystąpił błąd podczas zapisywania");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edytuj kredyty klienta</DialogTitle>
        </DialogHeader>

        <DialogBody className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            {credits.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                Brak kredytów. Dodaj pierwszy kredyt.
              </p>
            ) : (
              credits.map((credit, index) => (
                <div
                  key={credit.id}
                  className="border border-gray-200 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">
                      Kredyt #{index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCredit(credit.id)}
                    >
                      <Trash2 className="w-4 h-4 text-error-600" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Bank"
                      value={credit.bank}
                      onChange={(e) =>
                        handleUpdateCredit(credit.id, { bank: e.target.value })
                      }
                      placeholder="Nazwa banku"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Typ kredytu
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        value={credit.type}
                        onChange={(e) =>
                          handleUpdateCredit(credit.id, { type: e.target.value })
                        }
                      >
                        <option value="Hipoteczny">Hipoteczny</option>
                        <option value="Gotówkowy">Gotówkowy</option>
                        <option value="Konsolidacyjny">Konsolidacyjny</option>
                        <option value="Samochodowy">Samochodowy</option>
                        <option value="Karta kredytowa">Karta kredytowa</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Pozostała kwota (PLN)"
                      type="number"
                      value={credit.remainingAmount}
                      onChange={(e) =>
                        handleUpdateCredit(credit.id, {
                          remainingAmount: Number(e.target.value),
                        })
                      }
                    />
                    <Input
                      label="Rata miesięczna (PLN)"
                      type="number"
                      value={credit.monthlyPayment}
                      onChange={(e) =>
                        handleUpdateCredit(credit.id, {
                          monthlyPayment: Number(e.target.value),
                        })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Oprocentowanie (%)"
                      type="number"
                      step="0.01"
                      value={credit.interestRate || ""}
                      onChange={(e) =>
                        handleUpdateCredit(credit.id, {
                          interestRate: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                    <Input
                      label="Pozostałe miesiące"
                      type="number"
                      value={credit.remainingMonths || ""}
                      onChange={(e) =>
                        handleUpdateCredit(credit.id, {
                          remainingMonths: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </div>
              ))
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={handleAddCredit}
            >
              <Plus className="w-4 h-4 mr-2" />
              Dodaj kredyt
            </Button>

            {error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Zapisz zmiany
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Scoring Editor
// ============================================

interface ScoringEditorProps {
  isOpen: boolean;
  onClose: () => void;
  scoring: ScoringResult;
  onSave: (scoring: ScoringResult) => Promise<void>;
  isSaving: boolean;
}

export function ScoringEditorDialog({
  isOpen,
  onClose,
  scoring: initialScoring,
  onSave,
  isSaving,
}: ScoringEditorProps) {
  const [scoring, setScoring] = useState<ScoringResult>(initialScoring);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setScoring(initialScoring);
  }, [initialScoring]);

  const handleSave = async () => {
    try {
      setError(null);
      await onSave({
        ...scoring,
        calculatedAt: new Date().toISOString(),
      });
      onClose();
    } catch (e) {
      setError((e as Error).message || "Wystąpił błąd podczas zapisywania");
    }
  };

  const getCategoryFromScore = (score: number): ScoringResult["category"] => {
    if (score >= 750) return "excellent";
    if (score >= 650) return "good";
    if (score >= 550) return "fair";
    return "poor";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edytuj scoring kredytowy</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <div>
              <Input
                label="Wynik punktowy (300-850)"
                type="number"
                min={300}
                max={850}
                value={scoring.score}
                onChange={(e) => {
                  const score = Number(e.target.value);
                  setScoring({
                    ...scoring,
                    score,
                    category: getCategoryFromScore(score),
                  });
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={scoring.category}
                onChange={(e) =>
                  setScoring({
                    ...scoring,
                    category: e.target.value as ScoringResult["category"],
                  })
                }
              >
                <option value="excellent">Doskonały (750+)</option>
                <option value="good">Dobry (650-749)</option>
                <option value="fair">Przeciętny (550-649)</option>
                <option value="poor">Słaby (&lt;550)</option>
              </select>
            </div>

            <Input
              label="DTI (Debt-to-Income) %"
              type="number"
              step="0.1"
              min={0}
              max={100}
              value={scoring.dti}
              onChange={(e) =>
                setScoring({ ...scoring, dti: Number(e.target.value) })
              }
            />

            <Input
              label="Margines budżetowy %"
              type="number"
              step="0.1"
              min={0}
              max={100}
              value={scoring.budgetMargin}
              onChange={(e) =>
                setScoring({ ...scoring, budgetMargin: Number(e.target.value) })
              }
            />

            {error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Zapisz zmiany
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Creditworthiness Editor
// ============================================

interface CreditworthinessEditorProps {
  isOpen: boolean;
  onClose: () => void;
  creditworthiness: CreditworthinessResult;
  onSave: (creditworthiness: CreditworthinessResult) => Promise<void>;
  isSaving: boolean;
}

export function CreditworthinessEditorDialog({
  isOpen,
  onClose,
  creditworthiness: initialData,
  onSave,
  isSaving,
}: CreditworthinessEditorProps) {
  const [data, setData] = useState<CreditworthinessResult>(initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleSave = async () => {
    try {
      setError(null);
      await onSave({
        ...data,
        calculatedAt: new Date().toISOString(),
      });
      onClose();
    } catch (e) {
      setError((e as Error).message || "Wystąpił błąd podczas zapisywania");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edytuj zdolność kredytową</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <Input
              label="Maksymalna kwota kredytu (PLN)"
              type="number"
              value={data.maxAmount}
              onChange={(e) =>
                setData({ ...data, maxAmount: Number(e.target.value) })
              }
            />

            <Input
              label="Maksymalna rata miesięczna (PLN)"
              type="number"
              value={data.maxMonthlyPayment}
              onChange={(e) =>
                setData({ ...data, maxMonthlyPayment: Number(e.target.value) })
              }
            />

            <Input
              label="Zalecany okres kredytowania (lata)"
              type="number"
              min={1}
              max={35}
              value={data.recommendedPeriod}
              onChange={(e) =>
                setData({ ...data, recommendedPeriod: Number(e.target.value) })
              }
            />

            {error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Zapisz zmiany
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Personal Data Editor
// ============================================

interface PersonalDataEditorProps {
  isOpen: boolean;
  onClose: () => void;
  personalData: PersonalDataSnapshot;
  onSave: (personalData: PersonalDataSnapshot) => Promise<void>;
  isSaving: boolean;
}

export function PersonalDataEditorDialog({
  isOpen,
  onClose,
  personalData: initialData,
  onSave,
  isSaving,
}: PersonalDataEditorProps) {
  const [data, setData] = useState<PersonalDataSnapshot>(initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleSave = async () => {
    try {
      setError(null);
      await onSave(data);
      onClose();
    } catch (e) {
      setError((e as Error).message || "Wystąpił błąd podczas zapisywania");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edytuj dane osobowe</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <Input
              label="Dochód miesięczny (PLN)"
              type="number"
              value={data.income || ""}
              onChange={(e) =>
                setData({
                  ...data,
                  income: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />

            <Input
              label="Wydatki miesięczne (PLN)"
              type="number"
              value={data.monthlyExpenses || ""}
              onChange={(e) =>
                setData({
                  ...data,
                  monthlyExpenses: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forma zatrudnienia
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={data.employmentType || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    employmentType: e.target.value || undefined,
                  })
                }
              >
                <option value="">-- Wybierz --</option>
                <option value="umowa_o_prace">Umowa o pracę</option>
                <option value="dzialalnosc">Działalność gospodarcza</option>
                <option value="umowa_zlecenie">Umowa zlecenie</option>
                <option value="umowa_o_dzielo">Umowa o dzieło</option>
                <option value="emerytura">Emerytura</option>
                <option value="renta">Renta</option>
              </select>
            </div>

            <Input
              label="Staż pracy (miesiące)"
              type="number"
              min={0}
              value={data.employmentDuration || ""}
              onChange={(e) =>
                setData({
                  ...data,
                  employmentDuration: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Wiek"
                type="number"
                min={18}
                max={100}
                value={data.age || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    age: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />

              <Input
                label="Osoby na utrzymaniu"
                type="number"
                min={0}
                value={data.dependents || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    dependents: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              />
            </div>

            {error && (
              <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            Zapisz zmiany
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
